import { ChangeDetectionStrategy, Component, effect, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { form, maxLength, minLength, required, validate } from '@angular/forms/signals';
import { UserResetPasswordDto } from '@workspace/data-types';
import { AbstractSignalFormComponent } from '../../../abstract';
import { AuraInputPasswordCheckerComponent } from '../../../inputs/password-checker/password-checker.component';

export interface PasswordConfirmModel {
  password: string;
  passwordConfirm: string;
}

@Component({
  selector: 'aura-password-confirm-form',
  imports: [AuraInputPasswordCheckerComponent],
  templateUrl: './password-confirm-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuraPasswordConfirmFormComponent extends AbstractSignalFormComponent<
  UserResetPasswordDto,
  PasswordConfirmModel
> {
  passwordControl = new FormControl<string | null>('');
  passwordConfirmControl = new FormControl<string | null>('');

  override model: WritableSignal<PasswordConfirmModel> = signal<PasswordConfirmModel>({
    password: '',
    passwordConfirm: '',
  });

  override formTree = form(this.model, (b) => {
    required(b.password);
    minLength(b.password, 8);
    maxLength(b.password, 30);
    required(b.passwordConfirm);
    validate(b.passwordConfirm, ({ value }) => {
      if (value() !== this.model().password) {
        return { kind: 'mustMatch', message: 'Les mots de passe ne correspondent pas' };
      }
      return null;
    });
  });

  constructor() {
    super();

    this.passwordControl.valueChanges.subscribe((v) => {
      this.model.update((m) => ({ ...m, password: v ?? '' }));
    });

    this.passwordConfirmControl.valueChanges.subscribe((v) => {
      this.model.update((m) => ({ ...m, passwordConfirm: v ?? '' }));
    });

    effect(() => {
      const { password, passwordConfirm } = this.model();
      if (this.passwordControl.value !== password) {
        this.passwordControl.setValue(password || null, { emitEvent: false });
      }
      if (this.passwordConfirmControl.value !== passwordConfirm) {
        this.passwordConfirmControl.setValue(passwordConfirm || null, { emitEvent: false });
      }
    });
  }

  protected override buildModelFromDto(dto: UserResetPasswordDto): PasswordConfirmModel {
    return {
      password: dto?.password ?? '',
      passwordConfirm: '',
    };
  }

  protected override buildDtoFromModel(model: PasswordConfirmModel): UserResetPasswordDto {
    return {
      password: model.password,
    };
  }
}
